import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';

// raw_text에서 JSON 파싱 시도하는 헬퍼 함수
function parseRawData(data) {
  if (data.raw_text && !data.body && !data.title && !data.topic && !data.story) {
    let text = data.raw_text;
    text = text.replace(/^```json\s*/, '').replace(/```\s*$/, '');
    try {
      return JSON.parse(text);
    } catch (e) {
      try {
        const lastBrace = text.lastIndexOf('}');
        if (lastBrace > 0) {
          return JSON.parse(text.substring(0, lastBrace + 1));
        }
      } catch (e2) {
        // 파싱 완전 실패
      }
      return { _rawFallback: text };
    }
  }
  return data;
}

// 마크다운/텍스트를 보기 좋게 렌더링
function RenderContent({ text }) {
  if (!text) return null;
  return (
    <div className="result-content" style={{ lineHeight: '1.9', fontSize: '16px' }}>
      <ReactMarkdown>{text}</ReactMarkdown>
    </div>
  );
}

function ResultSection({ result, mode, originalText }) {
  const [view, setView] = useState('result');
  const rawData = result.result || {};
  const data = parseRawData(rawData);

  const modeLabels = {
    easy: '쉬운 해설',
    story: '단편소설',
    debate: '찬반 토론',
    level: '난이도 변환',
  };

  return (
    <section className="result-section">
      <div className="result-header">
        <h3>{modeLabels[mode] || '변환 결과'}</h3>
        <div className="view-toggle">
          <button
            className={view === 'result' ? 'active' : ''}
            onClick={() => setView('result')}
          >
            변환문
          </button>
          <button
            className={view === 'original' ? 'active' : ''}
            onClick={() => setView('original')}
          >
            원문
          </button>
        </div>
      </div>

      {view === 'result' && (
        <div className="result-body">
          {data._rawFallback ? (
            <RenderContent text={data._rawFallback} />
          ) : (
            <>
              {mode === 'easy' && <EasyResult data={data} />}
              {mode === 'story' && <StoryResult data={data} />}
              {mode === 'debate' && <DebateResult data={data} />}
              {mode === 'level' && <LevelResult data={data} />}
            </>
          )}

          {data._fact_check && <FactCheckDisplay check={data._fact_check} />}
          {result.cost && <CostDisplay cost={result.cost} />}
        </div>
      )}

      {view === 'original' && (
        <div className="original-article">
          <h4>원문 기사</h4>
          {result.original_title && (
            <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '12px' }}>
              {result.original_title}
            </h2>
          )}
          <div className="original-text">
            {originalText || '원문 텍스트가 없습니다.'}
          </div>
        </div>
      )}
    </section>
  );
}


function EasyResult({ data }) {
  return (
    <>
      {data.title && <h2 className="result-title">{data.title}</h2>}
      {data.subtitle && <p className="result-subtitle">{data.subtitle}</p>}
      {data.body && <RenderContent text={data.body} />}

      {data.why_it_matters && (
        <div style={{
          marginTop: '20px', padding: '16px', background: '#fffbeb',
          borderRadius: '8px', borderLeft: '4px solid #f59e0b'
        }}>
          <strong style={{ fontSize: '14px' }}>왜 중요한가?</strong>
          <p style={{ marginTop: '8px', fontSize: '15px', lineHeight: '1.7' }}>
            {data.why_it_matters}
          </p>
        </div>
      )}

      {data.key_terms && data.key_terms.length > 0 && (
        <div className="terms-section">
          <h4>핵심 용어 해설</h4>
          {data.key_terms.map((t, i) => (
            <div className="term-item" key={i}>
              <span className="term-name">{t.term}</span>
              <span>{t.explanation}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}


function StoryResult({ data }) {
  return (
    <>
      {data.title && <h2 className="result-title">{data.title}</h2>}

      {data.characters && (
        <div style={{
          display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap'
        }}>
          {data.characters.map((c, i) => (
            <span key={i} style={{
              padding: '6px 14px', background: '#f1f5f9',
              borderRadius: '20px', fontSize: '13px'
            }}>
              <strong>{c.name}</strong> — {c.description}
            </span>
          ))}
        </div>
      )}

      {data.story && <RenderContent text={data.story} />}

      {data.fact_check && data.fact_check.length > 0 && (
        <div style={{
          marginTop: '24px', padding: '16px', background: '#f0fdf4',
          borderRadius: '8px', borderLeft: '4px solid #10b981'
        }}>
          <strong style={{ fontSize: '14px' }}>팩트 체크</strong>
          {data.fact_check.map((f, i) => (
            <p key={i} style={{ marginTop: '6px', fontSize: '13px', lineHeight: '1.6' }}>
              ✓ {f}
            </p>
          ))}
        </div>
      )}
    </>
  );
}


function DebateResult({ data }) {
  return (
    <>
      {data.topic && <h2 className="result-title">{data.topic}</h2>}
      {data.background && <p className="result-subtitle">{data.background}</p>}

      {data.pro_arguments && (
        <div className="debate-section pro">
          <h4>찬성 측 논거</h4>
          {data.pro_arguments.map((arg, i) => (
            <div className="debate-point" key={i}>
              <strong>{i + 1}. {arg.point}</strong>
              <p>{arg.detail}</p>
            </div>
          ))}
        </div>
      )}

      {data.con_arguments && (
        <div className="debate-section con">
          <h4>반대 측 논거</h4>
          {data.con_arguments.map((arg, i) => (
            <div className="debate-point" key={i}>
              <strong>{i + 1}. {arg.point}</strong>
              <p>{arg.detail}</p>
            </div>
          ))}
        </div>
      )}

      {data.think_about && (
        <div style={{
          marginTop: '20px', padding: '16px', background: '#faf5ff',
          borderRadius: '8px', borderLeft: '4px solid #8b5cf6', textAlign: 'center'
        }}>
          <strong style={{ fontSize: '14px' }}>생각해볼 점</strong>
          <p style={{ marginTop: '8px', fontSize: '16px', fontStyle: 'italic' }}>
            {data.think_about}
          </p>
        </div>
      )}
    </>
  );
}


function LevelResult({ data }) {
  const diffLabels = {
    elementary: '초등학생', middle_school: '중학생',
    high_school: '고등학생', adult: '일반 성인', expert: '전문가'
  };

  return (
    <>
      {data.title && <h2 className="result-title">{data.title}</h2>}
      {data.difficulty && (
        <span style={{
          display: 'inline-block', padding: '4px 12px', background: '#fef3c7',
          borderRadius: '12px', fontSize: '13px', fontWeight: 600, marginBottom: '16px'
        }}>
          {diffLabels[data.difficulty] || data.difficulty} 수준
        </span>
      )}
      {data.body && <RenderContent text={data.body} />}

      {data.terms_explained && data.terms_explained.length > 0 && (
        <div className="terms-section">
          <h4>용어 설명</h4>
          {data.terms_explained.map((t, i) => (
            <div className="term-item" key={i}>
              <span className="term-name">{t.term}</span>
              <span>{t.explanation}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}


function FactCheckDisplay({ check }) {
  const passed = check.verification === 'passed';
  return (
    <div className={`fact-check-section ${passed ? '' : 'warning'}`}>
      <h4>{passed ? '팩트 검증 통과' : '팩트 검증 주의'}</h4>
      <p style={{ fontSize: '13px' }}>
        {passed
          ? '보호된 모든 팩트가 변환된 기사에 정확히 보존되었습니다.'
          : '일부 팩트의 보존 여부를 확인해주세요.'}
      </p>
    </div>
  );
}


function CostDisplay({ cost }) {
  if (!cost) return null;
  const krw = (cost.estimated_cost_usd * 1400).toFixed(1);

  return (
    <div className="cost-info">
      <span>모델: {cost.model_used}</span>
      <span>입력: {cost.input_tokens?.toLocaleString()}토큰</span>
      <span>출력: {cost.output_tokens?.toLocaleString()}토큰</span>
      <span>비용: ${cost.estimated_cost_usd?.toFixed(4)} (약 {krw}원)</span>
    </div>
  );
}


export default ResultSection;
